/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { Controller, useForm } from 'react-hook-form'
import {
  doc,
  collection,
  DocumentSnapshot,
  addDoc,
  updateDoc,
  onSnapshot,
  getDocs,
  query,
  where
} from 'firebase/firestore'
import { db } from '../../../firebase'
import {
  Box,
  Button,
  Typography,
  Backdrop,
  CircularProgress,
  Modal,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Container,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel
} from "@mui/material"
import dayjs, { Dayjs } from "dayjs"
import { toast } from 'react-toastify'
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import isEmpty from "lodash.isempty"
import { useParts } from "../../../components/hooks/useParts"

type Props = {
  individualNo: string
  productId?: string
  isOpen: boolean
  onClose: () => void
}

const ProcuctForm = (props: Props) => {
  const { individualNo, productId, isOpen, onClose } = props
  const { control, handleSubmit, reset, watch } = useForm({})
  const [open, setOpen] = useState<boolean>(false)
  const [prosessing, setProcessing] = useState<boolean>(false)
  const [formValues, setFormValues] = useState<Record<string, any>|null>()
  const productsRef = collection(db, 'products')
  const watchedType = watch('type') as string | undefined
  const parts = useParts(watchedType || 'エゾシカ')

  useEffect(() => {
    if (isEmpty(formValues)) {
      const today = dayjs()
      const defaultExpirationDate = today.add(1, 'year').subtract(1, 'day')
      reset({
        processingDate: today,
        type: "エゾシカ",
        part: "",
        weight: null,
        storageType: "冷凍",
        expirationDate: defaultExpirationDate,
        memo: ""
      })
    } else {
      reset({
        processingDate: dayjs(formValues.processingDate),
        type: formValues.type,
        part: formValues.part,
        weight: parseInt(formValues.weight, 10),
        storageType: formValues.storageType || "冷凍",
        expirationDate: dayjs(formValues.expirationDate),
        memo: formValues.memo
      })
    }

  }, [formValues])

  useEffect(() => {
    setOpen(isOpen)
    if (!productId) {
      setFormValues({})
      reset()
      return
    }
    onSnapshot(doc(productsRef, productId), (_: DocumentSnapshot) => {
      if (_.exists()) {
        const data = _.data()
        setFormValues(data)
      }
    })
  }, [productId, isOpen])

  const submit = async ({processingDate, type, part, weight, storageType, expirationDate, memo = ""}: Record<string, any>) => {
    setProcessing(true)
    const updateParams = {
      processingDate: processingDate.format("YYYY/MM/DD"),
      type,
      part,
      weight: parseInt(weight, 10),
      storageType,
      expirationDate: expirationDate.format("YYYY/MM/DD"),
      memo,
      isSold: false
    }
    try {
      if (productId) {
        await updateDoc(doc(productsRef, productId), {... updateParams, updatedAt: dayjs().format('YYYY/MM/DD hh:mm')})
      } else {
        const productsSnap = await getDocs(query(productsRef, where('individualNo', '==', individualNo)))
        const maxNumericPart = productsSnap.docs.reduce((max, d) => {
          const no: string = d.data().productNo || ''
          const numericPart = parseInt(no.substring(individualNo.length))
          return Math.max(max, isNaN(numericPart) ? 0 : numericPart)
        }, 0)
        const productNo = individualNo + ('000' + (maxNumericPart + 1)).slice(-3)
        await addDoc(productsRef, {... updateParams, individualNo, productNo, createdAt: dayjs().format('YYYY/MM/DD hh:mm')})
      }
      toast.success("製品情報を登録しました")
    }
    catch (e) {
      toast.error(JSON.stringify(e))
    }
    finally {
      reset()
      setProcessing(false)
      setOpen(false)
      onClose()
    }
  }

  const close = () => {
    setOpen(false)
    onClose()
  }

  const calculateExpirationDate = (processingDate: Dayjs, storageType: string) => {
    if (storageType === "チルド") {
      return processingDate.add(14, 'day')
    } else {
      // 冷凍の場合
      return processingDate.add(1, 'year').subtract(1, 'day')
    }
  }

  const onChangeProcessingDate = (v: Dayjs, storageType?: string) => {
    const currentStorageType = storageType || formValues?.storageType || "冷凍"
    const expirationDate = calculateExpirationDate(v, currentStorageType)
    setFormValues({ ...formValues, processingDate: v, expirationDate})
  }

  const onChangeStorageType = (storageType: string) => {
    const processingDate = formValues?.processingDate || dayjs()
    const expirationDate = calculateExpirationDate(processingDate, storageType)
    setFormValues({
      ...formValues,
      storageType,
      expirationDate,
      processingDate,
      type: formValues?.type || "エゾシカ",
      part: formValues?.part || "",
      weight: formValues?.weight || null,
      memo: formValues?.memo || ""
    })
  }

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClose={onClose}
      sx={{ padding: 2 }}
    >
      <Container maxWidth="xs" sx={{ mt: 6 }}>
        <Box
          component="form"
          onSubmit={handleSubmit(submit)}
          display="flex-col"
          justifyContent="center"
          alignItems="center"
          bgcolor="white"
        >
          <Box
            mx={2}
            width="50vh"
            display="flex-col"
          >
            <Box display="flex" justifyContent="start" m={1}>
              <Typography variant="h5" mt={1}>{`個体識別番号：${individualNo}`}</Typography>
            </Box>
          </Box>
          <Stack spacing={2} border={1} borderRadius={1} borderColor="#333" p={2} mx={2}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
              <Controller
                name="processingDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) {
                      return '加工日の入力は必須です'
                    }
                    if (!dayjs(value).isValid()) {
                      return '日付形式が間違っています'
                    }
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="ja"
                  >
                    <DatePicker
                      {...field}
                      label="加工日"
                      value={field.value}
                      format="YYYY/MM/DD"
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message
                        }
                      }}
                      onChange={(v) => onChangeProcessingDate(v)}
                    />
                  </LocalizationProvider>
                )}
              />
              <Controller
                name="storageType"
                control={control}
                defaultValue="冷凍"
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">保存方法</FormLabel>
                    <RadioGroup
                      {...field}
                      row
                      onChange={(e) => {
                        field.onChange(e.target.value)
                        onChangeStorageType(e.target.value)
                      }}
                    >
                      <FormControlLabel value="冷凍" control={<Radio />} label="冷凍" />
                      <FormControlLabel value="チルド" control={<Radio />} label="チルド" />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Controller
                name="type"
                control={control}
                defaultValue="エゾシカ"
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel id="select-label">獣種</InputLabel>
                    <Select
                      labelId="select-label"
                      id="select"
                      label="獣種"
                      {...field}
                    >
                      <MenuItem value="エゾシカ">エゾシカ</MenuItem>
                      <MenuItem value="ヒグマ">ヒグマ</MenuItem>
                    </Select>
                    <FormHelperText>{error?.message || ""}</FormHelperText>
                  </FormControl>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Controller
                name="part"
                control={control}
                rules={{
                  validate: (form) => {
                    if (!form) {
                      return '選択をしてください'
                    }
                  }
                }}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel id="select-label">部位</InputLabel>
                    <Select
                      labelId="select-label"
                      id="select"
                      label="部位"
                      {...field}
                    >
                      {
                        parts.map(part => {
                          return <MenuItem key={part} value={part}>{part}</MenuItem>
                        })
                      }
                    </Select>
                    <FormHelperText>{error?.message || ""}</FormHelperText>
                  </FormControl>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Controller
                name="weight"
                control={control}
                defaultValue=""
                rules={{
                  required: { value: true, message: '重量の入力は必須です' }
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="重量(g)"
                    fullWidth
                    placeholder="999"
                    error={!!error}
                    helperText={error && error.message}
                  />
                )}
              />
            </Box>
            <Box display="flex" justifyContent="start">
              <Controller
                name="expirationDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) {
                      return '消費期限の入力は必須です'
                    }
                    if (!dayjs(value).isValid()) {
                      return '日付形式が間違っています'
                    }
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="ja"
                  >
                    <DatePicker
                      {...field}
                      label="消費期限"
                      value={field.value}
                      format="YYYY/MM/DD"
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message
                        }
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Controller
                name="memo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    label="備考"
                    fullWidth
                    placeholder="備考"
                  />
                )}
              />
            </Box>
          </Stack>
          <Box m={1} p={1} display="flex" justifyContent="center">
            <Button variant="outlined" color="warning" onClick={close}>キャンセル</Button>
            <Button variant="contained" color="info" onClick={handleSubmit(submit)} sx={{ ml: 2 }}>登録</Button>
          </Box>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={prosessing}
            onClick={() => setProcessing(false)}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      </Container>
    </Modal>

  )
}
export default ProcuctForm
